/**
 * Authentication Context
 * Manages user authentication state across the application
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService, User, LoginCredentials, RegisterData } from '../services/authService';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<{ success: boolean; error?: string }>;
  register: (data: RegisterData) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  loginWithGoogle: (token: string) => Promise<{ success: boolean; error?: string }>;
  loginWithFacebook: (token: string) => Promise<{ success: boolean; error?: string }>;
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize authentication state on mount
  useEffect(() => {
    const initAuth = async () => {
      const token = authService.getToken();
      
      if (token) {
        // Verify token and get user data
        const isValid = await authService.verifyToken();
        
        if (isValid) {
          const userData = authService.getCurrentUser();
          setUser(userData);
        }
      }
      
      setIsLoading(false);
    };

    initAuth();
  }, []);

  const login = async (credentials: LoginCredentials) => {
    setIsLoading(true);
    
    try {
      const response = await authService.login(credentials);
      
      if (response.success && response.data) {
        setUser(response.data.user);
        setIsLoading(false);
        return { success: true };
      }
      
      setIsLoading(false);
      return { 
        success: false, 
        error: response.error || 'Login failed' 
      };
    } catch (error) {
      setIsLoading(false);
      return { 
        success: false, 
        error: 'An unexpected error occurred' 
      };
    }
  };

  const register = async (data: RegisterData) => {
    setIsLoading(true);
    
    try {
      const response = await authService.register(data);
      
      if (response.success && response.data) {
        setUser(response.data.user);
        setIsLoading(false);
        return { success: true };
      }
      
      setIsLoading(false);
      return { 
        success: false, 
        error: response.error || 'Registration failed' 
      };
    } catch (error) {
      setIsLoading(false);
      return { 
        success: false, 
        error: 'An unexpected error occurred' 
      };
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  const loginWithGoogle = async (token: string) => {
    setIsLoading(true);
    
    try {
      const response = await authService.loginWithGoogle(token);
      
      if (response.success && response.data) {
        setUser(response.data.user);
        setIsLoading(false);
        return { success: true };
      }
      
      setIsLoading(false);
      return { 
        success: false, 
        error: response.error || 'Google login failed' 
      };
    } catch (error) {
      setIsLoading(false);
      return { 
        success: false, 
        error: 'An unexpected error occurred' 
      };
    }
  };

  const loginWithFacebook = async (token: string) => {
    setIsLoading(true);
    
    try {
      const response = await authService.loginWithFacebook(token);
      
      if (response.success && response.data) {
        setUser(response.data.user);
        setIsLoading(false);
        return { success: true };
      }
      
      setIsLoading(false);
      return { 
        success: false, 
        error: response.error || 'Facebook login failed' 
      };
    } catch (error) {
      setIsLoading(false);
      return { 
        success: false, 
        error: 'An unexpected error occurred' 
      };
    }
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    loginWithGoogle,
    loginWithFacebook,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}

